
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle, Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { registerForEarlyAccess, testSupabaseConnection } from "@/utils/earlyAccess";
import EarlyAccessSuccessView from "./EarlyAccessSuccessView";
import EarlyAccessForm from "./EarlyAccessForm";

interface EarlyAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EarlyAccessModal: React.FC<EarlyAccessModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      toast.error(t("earlyAccess.errors.invalidEmail.title"), {
        description: t("earlyAccess.errors.invalidEmail.description"),
        duration: 4000,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log("=== EARLY ACCESS REGISTRATION ===");
      console.log("Email submitted:", email);
      
      const result = await registerForEarlyAccess(email);
      console.log("Registration result:", result);
      
      if (result.success) {
        setIsSuccess(true);
        toast.success(t("earlyAccess.success.title"), {
          description: t("earlyAccess.success.description"),
          duration: 6000,
        });
        
        // Reset email but keep success state until user closes modal
        setEmail("");
      } else {
        // Enhanced error handling with specific feedback
        if (result.error === 'email-exists') {
          toast.warning(t("earlyAccess.errors.emailExists.title"), {
            description: t("earlyAccess.errors.emailExists.description"),
            duration: 5000,
          });
        } else if (result.error === 'network-error' || result.error === 'connection-error') {
          toast.error(t("earlyAccess.errors.networkError.title"), {
            description: t("earlyAccess.errors.networkError.description"),
            duration: 6000,
          });
        } else if (result.error === 'auth-error') {
          toast.error(t("earlyAccess.errors.authError.title"), {
            description: t("earlyAccess.errors.authError.description"),
            duration: 5000,
          });
        } else {
          toast.error(t("earlyAccess.errors.temporaryError.title"), {
            description: result.message || t("earlyAccess.errors.temporaryError.description"),
            duration: 4000,
          });
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      
      toast.error(t("earlyAccess.errors.unexpectedError.title"), {
        description: t("earlyAccess.errors.unexpectedError.description"),
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail("");
      setIsSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass-card sm:max-w-[500px] max-w-[95vw] w-[95vw] sm:w-auto backdrop-blur-2xl border border-white/20 bg-propmatch-dark/95 shadow-2xl mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-propmatch-blue to-propmatch-blue-light rounded-full flex items-center justify-center mb-3 sm:mb-4 shadow-lg">
            {isSuccess ? (
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            ) : (
              <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            )}
          </div>
          <DialogTitle className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black bg-gradient-to-r from-white via-propmatch-blue-light to-white bg-clip-text text-transparent leading-tight">
            {isSuccess ? t("earlyAccess.modal.successTitle") : t("earlyAccess.modal.title")}
          </DialogTitle>
          <DialogDescription className="mt-2 sm:mt-3 text-sm sm:text-base text-propmatch-text-light leading-relaxed px-1 sm:px-2">
            {isSuccess 
              ? t("earlyAccess.modal.successDescription")
              : t("earlyAccess.modal.description")
            }
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <EarlyAccessSuccessView onClose={handleClose} />
        ) : (
          <EarlyAccessForm
            email={email}
            setEmail={setEmail}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EarlyAccessModal;
